/* eslint-disable @typescript-eslint/no-shadow */
import axios from "axios";
import { createJSONStorage, devtools, persist } from "zustand/middleware";
import { shallow } from "zustand/shallow";
import { createWithEqualityFn } from "zustand/traditional";
import { setHostname } from "utils/hostname";
import { STORAGE_AUTH_TOKEN } from "constants/variables";
import { getLocalStorageItem } from "utils/localStorage";
import { NotificationItem } from "types/Notifications";

interface INotificationsStore {
  canShowNext: boolean;
  setCurrentNotification: (
    notification: NotificationItem | null,
    delay?: number,
  ) => void;
  currentNotification: NotificationItem | null;
  notifications: NotificationItem[];
  addNotification: (notification: NotificationItem) => void;
  removeNotification: (id: number) => void;
  receivedNotification: (id: number) => void;
  resetNotifications: () => void;
}

export const normalizeNotifications = (
  newNotifications,
): NotificationItem[] => {
  return (
    newNotifications
      // Сортируем нотификации по весу, для приоритета отображения
      .sort((a, b) => a.weight - b.weight)
  );
};

const initialState = {
  canShowNext: true,
  currentNotification: null,
  notifications: [],
};

export const useNotificationsStore =
  createWithEqualityFn<INotificationsStore>()(
    devtools(
      persist(
        (set) => ({
          canShowNext: true,
          currentNotification: null,
          notifications: [],
          addNotification: (notification: NotificationItem) => {
            set((state) => {
              const ids = new Set(state.notifications.map((item) => item.id));

              if (!ids.has(notification.id) && notification.status) {
                // Отправялем на вычитку нотификацию как только пришла
                state.receivedNotification(notification.id);

                // Запускаем таймер, если лайфтайм пройдет, то нотификация должна удалиться из очереди
                setTimeout(() => {
                  if (
                    state.notifications.find(
                      (stateNotification) =>
                        stateNotification.id === notification.id,
                    )
                  ) {
                    state.removeNotification(notification.id);
                  }
                }, notification.lifeTime);

                // Если пришел триггер BonusActivationTerm24, ставим ему disabled
                // в ожидании триггера BonusCampaignAvailable
                if (notification?.kind === "BonusActivationTerm24") {
                  const bonusCampaignAvailable = state.notifications.find(
                    (findNotification) =>
                      findNotification.kind === "BonusCampaignAvailable",
                  );

                  return {
                    notifications: [
                      ...state.notifications,
                      { ...notification, disabled: !!bonusCampaignAvailable },
                    ],
                  };
                }

                // Если пришел триггер BonusCampaignAvailable, разрешаем отображать все
                // триггеры BonusActivationTerm24
                if (notification?.kind === "BonusCampaignAvailable") {
                  return {
                    notifications: [
                      ...state.notifications.map((notification) => {
                        if (notification.kind === "BonusActivationTerm24") {
                          return { ...notification, disabled: false };
                        }
                        return notification;
                      }),
                      notification,
                    ],
                  };
                }

                const hasNotificationUtilizationBalance =
                  state.notifications.find(
                    (findNotification) =>
                      findNotification.kind === "SystemBalanceUtilization",
                  );

                // Логика с отображением только одного попапа утилизации баланса
                // Если в сторе есть уже один, то остальные читаем и не добавляем
                if (hasNotificationUtilizationBalance) {
                  return {
                    notifications: state.notifications,
                  };
                }

                const depositNotificationId = state.notifications.findIndex(
                  (findNotification) =>
                    findNotification.kind === "SystemSuccessfulDeposit",
                );

                // Логика с суммированием депозитов в модалках
                if (
                  notification.kind === "SystemSuccessfulDeposit" &&
                  depositNotificationId !== -1
                ) {
                  // Суммируем вэлью из модалок, показываем одну
                  state.notifications[depositNotificationId] = {
                    ...state.notifications[depositNotificationId],
                    customData: {
                      ...state.notifications[depositNotificationId]?.customData,
                      amount:
                        Number(
                          state.notifications[depositNotificationId]?.customData
                            .amount,
                        ) + Number(notification.customData.amount),
                    },
                  };

                  return {
                    notifications: state.notifications,
                  };
                }

                return {
                  notifications: [...state.notifications, notification],
                };
              }

              return {
                notifications: state.notifications,
              };
            });
          },
          setCurrentNotification: (currentNotification, delay) => {
            // Если передан null - то это закрытие модального окна
            if (currentNotification === null) {
              // Запускаем таймер delay, который отвечает через
              // сколько покажется следующий попап
              setTimeout(() => {
                set(() => ({
                  canShowNext: true,
                }));
              }, delay ?? 0);
            }

            set({ currentNotification });
          },
          removeNotification: (id) => {
            set((state) => {
              // Когда удаляем нотификацию, устанавливаем флаг canShowNext в false
              return {
                canShowNext: false,
                notifications: state.notifications.filter(
                  (notification) => notification.id !== id,
                ),
              };
            });
          },

          receivedNotification: async (notificationId: number) => {
            try {
              await axios.post(
                `${setHostname()}/notifications/received`,
                {
                  lastReceivedNotificationId: Number(notificationId),
                },
                {
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${getLocalStorageItem(STORAGE_AUTH_TOKEN)}`,
                  },
                },
              );
              // eslint-disable-next-line no-empty
            } catch (error) {}
          },
          resetNotifications: () => {
            set(() => initialState);
          },
        }),
        {
          name: "notification-storage",
          storage: createJSONStorage(() => localStorage),
          partialize: (state) => ({ notifications: state.notifications }),
        },
      ),
      shallow,
    ),
  );
